import { z } from 'zod';
import type { FieldErrors, JsonSchema } from './SchemaForm.types';
import { getTypeName, isNullable, unwrapNullable } from './schemaUtils';

export interface SchemaValidationMessages {
  required: string;
  invalidText: string;
  invalidNumber: string;
  invalidBoolean: string;
  notAnOption: string;
  tooFewItems: (min: number) => string;
  tooManyItems: (max: number) => string;
  tooShort: (min: number) => string;
  tooLong: (max: number) => string;
  tooSmall: (min: number) => string;
  tooLarge: (max: number) => string;
}

type Messages = SchemaValidationMessages;

/**
 * A value the person has not supplied. An empty string counts because clearing a
 * text control is how someone unsets it, and a required field that reads as blank
 * should say so rather than pass because the key technically exists.
 */
function isBlank(value: unknown): boolean {
  return value === undefined || value === '';
}

function stringZod(schema: JsonSchema, messages: Messages): z.ZodType {
  let result = z.string({ error: () => messages.invalidText });
  if (schema.minLength !== undefined) {
    result = result.min(schema.minLength, messages.tooShort(schema.minLength));
  }
  if (schema.maxLength !== undefined) {
    result = result.max(schema.maxLength, messages.tooLong(schema.maxLength));
  }
  return result;
}

function numberZod(schema: JsonSchema, messages: Messages): z.ZodType {
  let result = z.number({ error: () => messages.invalidNumber });
  if (schema.minimum !== undefined) {
    result = result.min(schema.minimum, messages.tooSmall(schema.minimum));
  }
  if (schema.maximum !== undefined) {
    result = result.max(schema.maximum, messages.tooLarge(schema.maximum));
  }
  return result;
}

function enumZod(values: readonly unknown[], messages: Messages): z.ZodType {
  return z.unknown().superRefine((value, ctx) => {
    if (!values.includes(value)) ctx.addIssue({ code: 'custom', message: messages.notAnOption });
  });
}

function arrayZod(schema: JsonSchema, messages: Messages): z.ZodType {
  const items = schema.items ?? {};
  let result = z.array(buildZod(items, messages));
  if (schema.minItems !== undefined) {
    result = result.min(schema.minItems, messages.tooFewItems(schema.minItems));
  }
  if (schema.maxItems !== undefined) {
    result = result.max(schema.maxItems, messages.tooManyItems(schema.maxItems));
  }
  return result;
}

function objectZod(schema: JsonSchema, messages: Messages): z.ZodType {
  const properties = schema.properties ?? {};
  const shape: Record<string, z.ZodType> = {};

  for (const [key, propertySchema] of Object.entries(properties)) {
    shape[key] = buildZod(propertySchema, messages).optional();
  }

  const requiredKeys = (schema.required ?? []).filter((key) => key in properties);
  const objectSchema = z.object(shape);
  if (requiredKeys.length === 0) return objectSchema;

  return objectSchema.superRefine((value, ctx) => {
    const record = value;
    for (const key of requiredKeys) {
      const propertySchema = properties[key];
      const propertyValue = record[key];

      // `withDefault` renders a schema `default` without storing it, so an
      // untouched required property is already showing a value. Only an
      // absence the person can see counts as missing.
      if (propertyValue === undefined && propertySchema.default !== undefined) continue;

      const missing =
        isBlank(propertyValue) || (propertyValue === null && !isNullable(propertySchema));
      if (missing) {
        ctx.addIssue({ code: 'custom', path: [key], message: messages.required });
      }
    }
  });
}

function recordZod(schema: JsonSchema, messages: Messages): z.ZodType {
  const valueSchema =
    typeof schema.additionalProperties === 'object' ? schema.additionalProperties : {};
  return z.record(z.string(), buildZod(valueSchema, messages));
}

/**
 * Anything outside the supported subset validates as "always fine". Half-checking
 * a construct we cannot render would surface errors nobody can act on.
 */
function buildZod(rawSchema: JsonSchema, messages: Messages): z.ZodType {
  const schema = unwrapNullable(rawSchema);
  const nullable = isNullable(rawSchema);
  const inner = buildInnerZod(schema, messages);
  return nullable ? inner.nullable() : inner;
}

function buildInnerZod(schema: JsonSchema, messages: Messages): z.ZodType {
  if (
    schema.$ref !== undefined ||
    schema.const !== undefined ||
    schema.oneOf !== undefined ||
    schema.allOf !== undefined ||
    schema.anyOf !== undefined
  ) {
    return z.unknown();
  }

  if (schema.enum !== undefined) return enumZod(schema.enum, messages);

  switch (getTypeName(schema)) {
    case 'string':
      return stringZod(schema, messages);
    case 'number':
    case 'integer':
      return numberZod(schema, messages);
    case 'boolean':
      return z.boolean({ error: () => messages.invalidBoolean });
    case 'array':
      return schema.items === undefined ? z.unknown() : arrayZod(schema, messages);
    case 'object':
      if (schema.properties !== undefined) return objectZod(schema, messages);
      return typeof schema.additionalProperties === 'object'
        ? recordZod(schema, messages)
        : z.unknown();
    default:
      return z.unknown();
  }
}

/**
 * Validates the whole form at once and returns messages keyed by dotted path
 * (`themeAndProfilePairs.0.theme`), which is the same key each rendered field
 * uses to look up its own errors.
 */
export function validateFormData(
  schema: JsonSchema,
  formData: Record<string, unknown>,
  messages: SchemaValidationMessages,
): FieldErrors {
  if (schema.properties === undefined) return {};

  const result = buildZod(schema, messages).safeParse(formData);
  if (result.success) return {};

  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.map((segment) => String(segment)).join('.');
    errors[path] = [...(errors[path] ?? []), issue.message];
  }
  return errors;
}
